import { ApiRef, createApiRef } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/catalog-client';
import { Kind, ToolEntity } from '@mb.io/catalog-model';
import { GetToolsRequest, GetToolsResponse, ToolboxTool } from './types';

/**
 * Interface for the toolbox plugin.
 *
 * @public
 */
interface ToolboxApi {
  /**
   * Returns a list of toolbox tools.
   *
   * @param request - The request query options
   */
  getTools(request?: GetToolsRequest): Promise<GetToolsResponse>;
}

export const toolboxApiRef: ApiRef<ToolboxApi> = createApiRef<ToolboxApi>({
  id: 'plugin.toolbox.api',
});

const toToolboxTool = (entity: ToolEntity): ToolboxTool => ({
  id: `${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`,
  title: entity.metadata.title,
  description: entity.metadata.description,
  type: entity.spec.type,
  url: entity.spec.links?.[0]?.url ?? '',
  tags: entity.metadata.tags,
  lifecycle: entity.spec.lifecycle,
});

const TOOL_API_VERSION = 'mercedes-benz.com/v1alpha1';
const TOOL_CATALOG_PAGE_SIZE = 100;

export class ToolsCatalogApi implements ToolboxApi {
  constructor(private readonly catalogApi: CatalogApi) {}

  async getTools(): Promise<GetToolsResponse> {
    const tools: ToolboxTool[] = [];
    let cursor: string | undefined;

    do {
      const response = await this.catalogApi.queryEntities(
        cursor
          ? { cursor, limit: TOOL_CATALOG_PAGE_SIZE }
          : {
              filter: { kind: Kind.TOOL, apiVersion: TOOL_API_VERSION },
              limit: TOOL_CATALOG_PAGE_SIZE,
              orderFields: [
                { field: 'metadata.namespace', order: 'asc' },
                { field: 'metadata.name', order: 'asc' },
              ],
              totalItems: 'exclude',
            },
      );
      tools.push(
        ...response.items
          .filter(entity => entity.apiVersion === TOOL_API_VERSION)
          .map(entity => toToolboxTool(entity as ToolEntity)),
      );
      cursor = response.pageInfo.nextCursor;
    } while (cursor);

    return { tools };
  }
}
